import React from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { formatNumber } from "../../../utils/formatNumber";

const POBreakdownGrid = ({ open, onClose, poData }) => {
  const columns = [
    { field: "purchaseOrderId", headerName: "PO #", width: 100 },
    { field: "subCategory", headerName: "หมวดหมู่ย่อย", width: 120 },
    { field: "product", headerName: "สินค้า", width: 120, flex: 1 },
    { field: "unit", headerName: "หน่วย", width: 80 },
    {
      field: "currentWeight",
      headerName: "น้ำหนักซื้อจากครั้งนี้",
      width: 180,
      align: "right",
    },
    {
      field: "poWeight",
      headerName: "น้ำหนักซื้อจริงทั้งหมด / น้ำหนัก PO",
      width: 240,
      align: "right",
    },
    {
      field: "overWeight",
      width: 160,
      align: "right",
      display: "flex",
      renderHeader: () => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
          <Typography variant="body2" color="error" fontWeight="medium">
            น้ำหนักขาด
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            /
          </Typography>
          <Typography variant="body2" color="warning.main" fontWeight="medium">
            เกิน
          </Typography>
        </Box>
      ),
      renderCell: (params) => {
        const value = Number(params.value || 0);
        const color = value < 0 ? "error" : value > 0 ? "warning" : "default";

        return (
          <Typography variant="body2" color={color} fontWeight={"medium"}>
            {formatNumber(value)}
          </Typography>
        );
      },
    },
  ];

  const summaryMap = new Map();
  const noSellRows = [];

  poData.forEach((poItem) => {
    poItem.details.forEach((detail) => {
      if (detail.priceSource !== "po") return;

      const po = detail.poInfo;
      const productId = po?.product?.id;
      const purchaseOrderId = po?.purchaseOrderId;
      const key = `${purchaseOrderId}-${productId}`;
      const poUsedWeight = detail.poUsedWeight || 0;
      const netWeight = poItem.originalItem?.netWeight || 0;
      const poWeight = po?.poWeight || 0;
      const unlimited = po?.unlimitedWeight;

      if (poUsedWeight === 0) {
        noSellRows.push({
          id: `${key}-nosell`,
          subCategory: po?.product?.subCategory?.name,
          purchaseOrderId: String(purchaseOrderId).padStart(4, "0"),
          product: po?.product?.title,
          unit: "กก.",
          currentWeight: 0,
          poWeight: `${formatNumber(netWeight)} / ${
            unlimited ? "ไม่จำกัด" : formatNumber(poWeight)
          }`,
          overWeight: unlimited ? 0 : netWeight - poWeight,
        });
      } else {
        if (!summaryMap.has(key)) {
          summaryMap.set(key, {
            id: key,
            subCategory: po?.product?.subCategory?.name,
            purchaseOrderId: String(purchaseOrderId).padStart(4, "0"),
            product: po?.product?.title,
            unit: "กก.",
            totalPoUsedWeight: 0,
            totalNetWeight: 0,
            totalPoWeight: 0,
            unlimited,
          });
        }

        const entry = summaryMap.get(key);
        entry.totalPoUsedWeight += poUsedWeight;
        entry.totalNetWeight += netWeight;
        entry.totalPoWeight += poWeight;
      }
    });
  });

  const rows = Array.from(summaryMap.values()).map((entry) => ({
    id: entry.id,
    subCategory: entry.subCategory,
    purchaseOrderId: entry.purchaseOrderId,
    product: entry.product,
    unit: entry.unit,
    currentWeight: formatNumber(entry.totalPoUsedWeight),
    poWeight: `${formatNumber(entry.totalNetWeight)} / ${
      entry.unlimited ? "ไม่จำกัด" : formatNumber(entry.totalPoWeight)
    }`,
    overWeight: entry.unlimited
      ? 0
      : entry.totalNetWeight - entry.totalPoWeight,
  }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>สรุปการซื้อ PO</DialogTitle>
      <DialogContent>
        <Box sx={{ width: "100%" }}>
          <Box sx={{ height: rows.length === 0 ? 200 : "auto" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              hideFooter
              disableRowSelectionOnClick
              localeText={{ noRowsLabel: "ไม่มีข้อมูล" }}
            />
          </Box>
        </Box>

        <Typography variant="h6" my={2}>
          รายการสินค้าที่ซัพพลายเออร์ไม่ได้นำมาขาย
        </Typography>

        <Box sx={{ width: "100%" }}>
          <Box sx={{ height: noSellRows.length === 0 ? 200 : "auto" }}>
            <DataGrid
              rows={noSellRows}
              columns={columns}
              hideFooter
              disableRowSelectionOnClick
              localeText={{ noRowsLabel: "ไม่มีข้อมูล" }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default POBreakdownGrid;
